import { memo } from "react";
import { Divider, Stack, styled, Typography } from "@schaeffler-commerce/design-system";
import { C_ON_SURFACE_LOW_EMPHASIS, C_SURFACE_CONTAINER_HIGHEST } from "@schaeffler-commerce/design-system";

import {
  TargetGraphTooltip,
  TargetGraphTooltipContent,
  TargetGraphTooltipEntry,
} from "~/features/cms/homepagePersonalization/cpd/components/bonusAgreementTile/components/targetGraph/TargetGraphTooltip";
import { makeFormatCurrency } from "~/features/cms/homepagePersonalization/cpd/components/bonusAgreementTile/bonusAgeementUtil";

export interface ProgressBarProps {
  /**
   * Maximum value for scaling the progress bar
   */
  maxValue: number;
  /**
   * Invoice value (light green section)
   */
  invoiceValue: number;
  /**
   * Orderbook value (light green + dark green sections)
   */
  orderbookValue: number;
  /**
   * All target values to determine if all are reached
   */
  targets?: { value: number; rate: number }[];
  /**
   * Gap to next target
   */
  gapToNextTarget?: number;

  currency: string;
}

const ProgressContainer = styled("div")({
  width: "100%",
  height: "24px",
  backgroundColor: C_SURFACE_CONTAINER_HIGHEST,
  borderRadius: "12px",
  position: "relative",
  overflow: "hidden",
  cursor: "pointer",
});

const Bar = styled("div")<{ $width: number; $color: string }>(({ $width, $color }) => ({
  height: "100%",
  backgroundColor: $color,
  position: "absolute",
  left: 0,
  width: `${$width}%`,
}));

const ProgressBar = ({
  maxValue,
  invoiceValue,
  orderbookValue,
  targets = [],
  gapToNextTarget = 0,
  currency,
}: ProgressBarProps) => {
  // Calculate percentages based on maxValue (last target = 100%)
  const invoicePercentage = Math.min((invoiceValue / maxValue) * 100, 100);
  const orderbookPercentage = Math.min(((invoiceValue + orderbookValue) / maxValue) * 100, 100);

  const totalValue = invoiceValue + orderbookValue;

  const formatCurrency = makeFormatCurrency(currency);

  const showValues = invoiceValue > 0 || orderbookValue > 0;
  const showTotal = invoiceValue > 0 && orderbookValue > 0;

  const tooltipContent = (
    <TargetGraphTooltipContent>
      {showTotal && (
        <Stack sx={{
          gap: 0.5
        }}>
          <Typography variant="bodySmall" color={C_ON_SURFACE_LOW_EMPHASIS}>
            Total
          </Typography>
          <Typography variant="titleLarge" sx={{
            color: "text.secondary"
          }}>
            {formatCurrency(totalValue)}
          </Typography>
        </Stack>
      )}
      {showValues && (
        <Stack sx={{
          gap: 0.5
        }}>
          {invoiceValue > 0 && (
            <TargetGraphTooltipEntry label="Invoiced value" value={formatCurrency(invoiceValue)} color={"#c9dea5"} />
          )}
          {orderbookValue > 0 && (
            <TargetGraphTooltipEntry
              label="Confirmed orderbook"
              value={formatCurrency(orderbookValue)}
              color={"#73b281"}
            />
          )}
        </Stack>
      )}

      {showValues && gapToNextTarget > 0 && <Divider />}

      {gapToNextTarget > 0 && (
        <TargetGraphTooltipEntry label="Gap to next target" value={formatCurrency(gapToNextTarget)} />
      )}
    </TargetGraphTooltipContent>
  );

  return (
    <TargetGraphTooltip title={tooltipContent}>
      <ProgressContainer>
        <Bar $width={orderbookPercentage} $color={"#73b281"} />
        <Bar $width={invoicePercentage} $color={"#c9dea5"} />
      </ProgressContainer>
    </TargetGraphTooltip>
  );
};

export default memo(ProgressBar);
